import { useState, useEffect } from "react";
import axios from "axios";
import { 
  StickyNote, 
  Plus, 
  Edit2, 
  Trash2, 
  X,
  Save,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotesSection({ entityType, entityId, entityName }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    tags: ""
  });

  useEffect(() => {
    if (entityType && entityId) {
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notes/?entity_type=${entityType}&entity_id=${entityId}`);
      setNotes(response.data?.notes || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.content.trim()) {
      toast.error("Note content is required");
      return;
    }

    try {
      const tags = formData.tags.split(",").map(t => t.trim()).filter(Boolean);
      
      if (editingNote) {
        await axios.put(`${API}/notes/${editingNote.id}`, {
          title: formData.title,
          content: formData.content,
          tags
        });
        toast.success("Note updated");
      } else {
        await axios.post(`${API}/notes/`, {
          entity_type: entityType,
          entity_id: entityId,
          title: formData.title,
          content: formData.content,
          tags
        });
        toast.success("Note added");
      }
      
      setShowAddDialog(false);
      setEditingNote(null);
      resetForm();
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Failed to save note");
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success("Note deleted");
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const openEditDialog = (note) => {
    setFormData({
      title: note.title || "",
      content: note.content,
      tags: (note.tags || []).join(", ")
    });
    setEditingNote(note);
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", tags: "" });
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="card overflow-hidden" data-testid={`notes-section-${entityType}-${entityId}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 bg-zinc-50 border-b cursor-pointer hover:bg-zinc-100 transition-colors">
            <div className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-zinc-800">Notes</h3>
              <Badge variant="secondary">{notes.length}</Badge>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                resetForm();
                setShowAddDialog(true);
              }}
              data-testid="add-note-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Note
            </Button>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="loading-spinner"></div>
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-6 text-zinc-500">
                <StickyNote className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No notes yet for {entityName || entityId}</p>
                <p className="text-xs mt-1">Add notes to track important information</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map(note => (
                  <div 
                    key={note.id} 
                    className="p-3 bg-amber-50/50 rounded-lg border border-amber-100"
                    data-testid={`note-item-${note.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {note.title && (
                          <h4 className="font-medium text-zinc-800 mb-1">{note.title}</h4>
                        )}
                        <p className="text-sm text-zinc-700 whitespace-pre-wrap">{note.content}</p>
                        
                        <div className="flex items-center gap-2 mt-2">
                          {note.tags?.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <Tag className="w-3 h-3 text-zinc-400" />
                              {note.tags.map((tag, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs py-0">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                          <span className="text-xs text-zinc-400 ml-auto">
                            {formatDate(note.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEditDialog(note)}
                          data-testid={`edit-note-${note.id}`}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                          onClick={() => handleDelete(note.id)}
                          data-testid={`delete-note-${note.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingNote ? "Edit Note" : `Add Note for ${entityName || entityId}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title (optional)</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="E.g., Registration steps"
                data-testid="note-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Content *</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                placeholder="Your note..."
                rows={4}
                data-testid="note-content-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Tags (comma-separated)</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="E.g., important, follow-up, research"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-note-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingNote ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
